import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import {
    Card,
    CardBody,
    Typography,
    Input,
    Button,
    Select,
    Option,
    Chip,
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Textarea
} from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faToggleOn, faToggleOff, faPlus, faTimes, faSave } from '@fortawesome/free-solid-svg-icons';
import Alerty from '../../common/Alerty';
const { rootUrl } = require('../../../config/config');

const ChartOfAccountsManager = () => {
    const { token, user } = useAuth();
    const authToken = token || localStorage.getItem('authToken') || 'default-token';

    // State
    const [accounts, setAccounts] = useState([]);
    const [, setFilteredAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [paginationPage, setPaginationPage] = useState(0);
    const [totalAccounts, setTotalAccounts] = useState(0);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [analysisCodes, setAnalysisCodes] = useState([]);
    const [analysisMappings, setAnalysisMappings] = useState([]);
    const [mappingLoading, setMappingLoading] = useState(false);
    const [mappingError, setMappingError] = useState('');
    const [mappingAnalysisCode, setMappingAnalysisCode] = useState('');
    const [mappingActive, setMappingActive] = useState(true);
    const [mappingSaving, setMappingSaving] = useState(false);
    const [mappingDeleteTarget, setMappingDeleteTarget] = useState(null);
    const [mappingDeleteLoading, setMappingDeleteLoading] = useState(false);
    const itemsPerPage = 20;

    // Filters
    const [filters, setFilters] = useState({
        type: '',
        glClass: '',
        isEnabled: '',
        search: ''
    });

    // Modals
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingAccount, setEditingAccount] = useState(null);

    // Form Data
    const [formData, setFormData] = useState({
        account_code: '',
        account_name: '',
        account_type: 'ASSET',
        sub_type: '',
        detail_type: '',
        gl_class: 'BALANCE_SHEET',
        normal_balance: 'DEBIT',
        description: '',
        input_by: user?.username || ''
    });

    // Alerts
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertColor, setAlertColor] = useState('blue');
    const [alertMsg, setAlertMsg] = useState('');

    // Errors
    const [errors, setErrors] = useState({});

    // Effects
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchAccounts();
    }, [paginationPage, filters]);

    useEffect(() => {
        const fetchMappingData = async () => {
            try {
                setMappingLoading(true);
                const headers = { Authorization: `Bearer ${authToken}` };
                const [analysisRes, mappingRes] = await Promise.all([
                    axios.get(`${rootUrl}/api/owner-analysis-codes`, { headers }),
                    axios.get(`${rootUrl}/api/system/gl-analysis-code-mappings`, { headers }),
                ]);

                setAnalysisCodes(Array.isArray(analysisRes.data?.data) ? analysisRes.data.data : []);
                setAnalysisMappings(Array.isArray(mappingRes.data?.data) ? mappingRes.data.data : []);
                setMappingError('');
            } catch (err) {
                setMappingError(err.response?.data?.message || 'Failed to load analysis mappings');
            } finally {
                setMappingLoading(false);
            }
        };

        fetchMappingData();
    }, [authToken]);

    // API Calls
    const fetchAccounts = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.type) params.append('type', filters.type);
            if (filters.glClass) params.append('gl_class', filters.glClass);
            if (filters.isEnabled !== '') params.append('is_enabled', filters.isEnabled === 'true');
            if (filters.search) params.append('search', filters.search);
            params.append('limit', itemsPerPage);
            params.append('offset', paginationPage * itemsPerPage);

            const response = await axios.get(
                `${rootUrl}/system/coa/accounts?${params.toString()}`,
                { headers: { 'Authorization': `Bearer ${authToken}` } }
            );

            if (response.data.success) {
                setAccounts(response.data.data || []);
                setTotalAccounts(response.data.total || 0);
            } else {
                setAccounts([]);
            }
        } catch (err) {
            showAlert('red', 'Failed to fetch COA accounts');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const createAccount = async () => {
        if (!validateForm()) return;

        try {
            const response = await axios.post(
                `${rootUrl}/system/coa/accounts`,
                formData,
                { headers: { 'Authorization': `Bearer ${authToken}` } }
            );

            if (response.data.success) {
                showAlert('green', 'Account created successfully');
                setShowCreateModal(false);
                resetForm();
                setPaginationPage(0);
                fetchAccounts();
            } else {
                showAlert('red', response.data.message || 'Failed to create account');
            }
        } catch (err) {
            showAlert('red', err.response?.data?.message || 'Error creating account');
            console.error(err);
        }
    };

    const updateAccount = async () => {
        if (!editingAccount) return;

        const updateData = {
            description: formData.description,
            is_enabled: formData.is_enabled !== undefined ? formData.is_enabled : editingAccount.is_enabled,
            sub_type: formData.sub_type,
            detail_type: formData.detail_type,
            edit_by: user?.username || 'system'
        };

        try {
            const response = await axios.patch(
                `${rootUrl}/system/coa/accounts/${editingAccount.account_code}`,
                updateData,
                { headers: { 'Authorization': `Bearer ${authToken}` } }
            );

            if (response.data.success) {
                showAlert('green', 'Account updated successfully');
                setShowEditModal(false);
                resetForm();
                fetchAccounts();
            } else {
                showAlert('red', response.data.message || 'Failed to update account');
            }
        } catch (err) {
            showAlert('red', err.response?.data?.message || 'Error updating account');
            console.error(err);
        }
    };

    const toggleAccountStatus = async (account, newStatus) => {
        try {
            const response = await axios.patch(
                `${rootUrl}/system/coa/accounts/${account.account_code}/toggle`,
                { is_enabled: newStatus, edit_by: user?.username || 'system' },
                { headers: { 'Authorization': `Bearer ${authToken}` } }
            );

            if (response.data.success) {
                showAlert('green', `Account ${newStatus ? 'enabled' : 'disabled'} successfully`);
                fetchAccounts();
            } else {
                showAlert('red', response.data.message || 'Failed to toggle status');
            }
        } catch (err) {
            showAlert('red', 'Error toggling account status');
            console.error(err);
        }
    };

    const refreshMappings = async () => {
        try {
            const headers = { Authorization: `Bearer ${authToken}` };
            const response = await axios.get(`${rootUrl}/api/system/gl-analysis-code-mappings`, { headers });
            setAnalysisMappings(Array.isArray(response.data?.data) ? response.data.data : []);
        } catch (err) {
            setMappingError(err.response?.data?.message || 'Failed to refresh analysis mappings');
        }
    };

    const handleViewMappings = (account) => {
        setSelectedAccount(account);
        setMappingAnalysisCode('');
        setMappingActive(true);
        setMappingDeleteTarget(null);
        setMappingError('');
    };

    const currentAccountMappings = useMemo(() => {
        if (!selectedAccount) return [];
        return analysisMappings.filter((mapping) => mapping.gl_account_code === selectedAccount.account_code);
    }, [analysisMappings, selectedAccount]);

    const availableAnalysisCodes = useMemo(() => {
        const usedCodes = new Set(currentAccountMappings.map((mapping) => mapping.analysis_code));
        return analysisCodes.filter((code) => !usedCodes.has(code.code));
    }, [analysisCodes, currentAccountMappings]);

    const createMapping = async () => {
        if (!selectedAccount) {
            setMappingError('Select a GL account first');
            return;
        }
        if (!mappingAnalysisCode) {
            setMappingError('Analysis code is required');
            return;
        }

        try {
            setMappingSaving(true);
            await axios.post(
                `${rootUrl}/api/system/gl-analysis-code-mappings`,
                {
                    analysis_code: mappingAnalysisCode,
                    gl_account_code: selectedAccount.account_code,
                    is_active: mappingActive,
                    input_by: user?.username || 'system',
                },
                { headers: { Authorization: `Bearer ${authToken}` } }
            );
            setMappingAnalysisCode('');
            setMappingActive(true);
            await refreshMappings();
        } catch (err) {
            setMappingError(err.response?.data?.message || 'Failed to create mapping');
        } finally {
            setMappingSaving(false);
        }
    };

    const deactivateMapping = async () => {
        if (!mappingDeleteTarget) return;

        try {
            setMappingDeleteLoading(true);
            await axios.delete(
                `${rootUrl}/api/system/gl-analysis-code-mappings/${mappingDeleteTarget.analysis_code}/${mappingDeleteTarget.gl_account_code}`,
                { headers: { Authorization: `Bearer ${authToken}` } }
            );
            setMappingDeleteTarget(null);
            await refreshMappings();
        } catch (err) {
            setMappingError(err.response?.data?.message || 'Failed to deactivate mapping');
        } finally {
            setMappingDeleteLoading(false);
        }
    };

    // Validation
    const validateForm = () => {
        const newErrors = {};

        if (!formData.account_code) {
            newErrors.account_code = 'Account code is required';
        } else if (!/^\d{5}$/.test(formData.account_code)) {
            newErrors.account_code = 'Account code must be exactly 5 digits';
        }

        if (!formData.account_name) {
            newErrors.account_name = 'Account name is required';
        }

        if (!formData.account_type) {
            newErrors.account_type = 'Account type is required';
        }

        if (!formData.gl_class) {
            newErrors.gl_class = 'GL class is required';
        }

        if (!formData.normal_balance) {
            newErrors.normal_balance = 'Normal balance is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // UI Helpers
    const showAlert = (color, message) => {
        setAlertColor(color);
        setAlertMsg(message);
        setAlertOpen(true);
    };

    const resetForm = () => {
        setFormData({
            account_code: '',
            account_name: '',
            account_type: 'ASSET',
            sub_type: '',
            detail_type: '',
            gl_class: 'BALANCE_SHEET',
            normal_balance: 'DEBIT',
            description: '',
            input_by: user?.username || ''
        });
        setErrors({});
        setEditingAccount(null);
    };

    const handleEdit = (account) => {
        setEditingAccount(account);
        setFormData({
            account_code: account.account_code,
            account_name: account.account_name,
            account_type: account.account_type,
            sub_type: account.sub_type || '',
            detail_type: account.detail_type || '',
            gl_class: account.gl_class,
            normal_balance: account.normal_balance,
            description: account.description || '',
            is_enabled: account.is_enabled
        });
        setShowEditModal(true);
    };

    const handleCreateOpen = () => {
        resetForm();
        setShowCreateModal(true);
    };

    const handleCreateClose = () => {
        setShowCreateModal(false);
        resetForm();
    };

    const handleEditClose = () => {
        setShowEditModal(false);
        resetForm();
    };

    const getAccountTypeColor = (type) => {
        switch (type) {
            case 'ASSET': return 'green';
            case 'LIABILITY': return 'red';
            case 'EQUITY': return 'blue';
            case 'REVENUE': return 'teal';
            case 'EXPENSE': return 'orange';
            default: return 'gray';
        }
    };

    const getStatusColor = (isEnabled) => {
        return isEnabled ? 'green' : 'gray';
    };

    const totalPages = Math.ceil(totalAccounts / itemsPerPage);

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
            <div className="p-6 lg:p-8">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">Chart of Accounts</h1>
                    <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full mb-4"></div>
                    <p className="text-lg text-[#2a2e30] opacity-80">
                        Manage GL account hierarchy, balances, and mappings
                    </p>
                </div>
            </div>

            <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 pb-8">
                <Alerty open={alertOpen} setOpen={setAlertOpen} color={alertColor} message={alertMsg} />

                {/* Filters & Controls */}
                <Card className="mb-6 shadow-xl rounded-lg">
                    <CardBody className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
                            <Input
                                label="Search"
                                value={filters.search}
                                onChange={(e) => {
                                    setFilters({ ...filters, search: e.target.value });
                                    setPaginationPage(0);
                                }}
                                placeholder="Code, name, or description"
                            />

                            <Select
                                label="Account Type"
                                value={filters.type}
                                onChange={(value) => {
                                    setFilters({ ...filters, type: value || '' });
                                    setPaginationPage(0);
                                }}
                            >
                                <Option value="">All Types</Option>
                                <Option value="ASSET">Asset</Option>
                                <Option value="LIABILITY">Liability</Option>
                                <Option value="EQUITY">Equity</Option>
                                <Option value="REVENUE">Revenue</Option>
                                <Option value="EXPENSE">Expense</Option>
                            </Select>

                            <Select
                                label="GL Class"
                                value={filters.glClass}
                                onChange={(value) => {
                                    setFilters({ ...filters, glClass: value || '' });
                                    setPaginationPage(0);
                                }}
                            >
                                <Option value="">All Classes</Option>
                                <Option value="BALANCE_SHEET">Balance Sheet</Option>
                                <Option value="INCOME_STATEMENT">Income Statement</Option>
                            </Select>

                            <Select
                                label="Status"
                                value={filters.isEnabled}
                                onChange={(value) => {
                                    setFilters({ ...filters, isEnabled: value || '' });
                                    setPaginationPage(0);
                                }}
                            >
                                <Option value="">All Status</Option>
                                <Option value="true">Enabled</Option>
                                <Option value="false">Disabled</Option>
                            </Select>

                            <Button
                                onClick={handleCreateOpen}
                                className="bg-[#FF5733] text-white rounded-sm px-4 py-2 hover:bg-[#e64520]"
                            >
                                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                                Add Account
                            </Button>
                        </div>
                    </CardBody>
                </Card>

                {/* Accounts Table */}
                <Card className="shadow-xl rounded-lg overflow-hidden">
                    <CardBody className="p-0">
                        {loading ? (
                            <div className="p-8 text-center">
                                <Typography>Loading accounts...</Typography>
                            </div>
                        ) : accounts.length === 0 ? (
                            <div className="p-8 text-center">
                                <Typography>No accounts found</Typography>
                            </div>
                        ) : (
                            <>
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-[#f5f5f5] border-b border-[#e0e0e0]">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-[#2a2e30] uppercase">Code</th>
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-[#2a2e30] uppercase">Name</th>
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-[#2a2e30] uppercase">Type</th>
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-[#2a2e30] uppercase">Class</th>
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-[#2a2e30] uppercase">Balance</th>
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-[#2a2e30] uppercase">Status</th>
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-[#2a2e30] uppercase">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {accounts.map((account) => (
                                                <tr key={account.account_code} className="border-b border-[#e0e0e0] hover:bg-[#f9f9f9] transition">
                                                    <td className="px-6 py-4 text-sm font-semibold text-[#2a2e30]">
                                                        {account.account_code}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-[#2a2e30]">
                                                        {account.account_name}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <Chip
                                                            value={account.account_type}
                                                            color={getAccountTypeColor(account.account_type)}
                                                            size="sm"
                                                        />
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-[#2a2e30]">
                                                        {account.gl_class === 'BALANCE_SHEET' ? 'BS' : 'IS'}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <Chip
                                                            value={account.normal_balance}
                                                            color={account.normal_balance === 'DEBIT' ? 'blue' : 'indigo'}
                                                            size="sm"
                                                        />
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <Chip
                                                            value={account.is_enabled ? 'Enabled' : 'Disabled'}
                                                            color={getStatusColor(account.is_enabled)}
                                                            size="sm"
                                                        />
                                                    </td>
                                                    <td className="px-6 py-4 text-sm space-x-2">
                                                        <Button
                                                            onClick={() => handleViewMappings(account)}
                                                            size="sm"
                                                            className="bg-[#FF5733] hover:bg-[#e64520] text-white rounded-sm px-3 py-1"
                                                        >
                                                            Mappings
                                                        </Button>
                                                        <Button
                                                            onClick={() => handleEdit(account)}
                                                            size="sm"
                                                            className="bg-blue-500 hover:bg-blue-600 text-white rounded-sm px-3 py-1"
                                                        >
                                                            <FontAwesomeIcon icon={faEdit} />
                                                        </Button>
                                                        <Button
                                                            onClick={() => toggleAccountStatus(account, !account.is_enabled)}
                                                            size="sm"
                                                            className={`${account.is_enabled ? 'bg-red-400' : 'bg-green-500'} hover:opacity-90 text-white rounded-sm px-3 py-1`}
                                                        >
                                                            <FontAwesomeIcon icon={account.is_enabled ? faToggleOn : faToggleOff} />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className="flex justify-between items-center px-6 py-4 border-t border-[#e0e0e0]">
                                        <Typography variant="small">
                                            Page {paginationPage + 1} of {totalPages} ({totalAccounts} total)
                                        </Typography>
                                        <div className="space-x-2">
                                            <Button
                                                onClick={() => setPaginationPage(Math.max(0, paginationPage - 1))}
                                                disabled={paginationPage === 0}
                                                size="sm"
                                                className="rounded-sm"
                                            >
                                                Previous
                                            </Button>
                                            <Button
                                                onClick={() => setPaginationPage(Math.min(totalPages - 1, paginationPage + 1))}
                                                disabled={paginationPage === totalPages - 1}
                                                size="sm"
                                                className="rounded-sm"
                                            >
                                                Next
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </CardBody>
                </Card>

                {selectedAccount && (
                    <Card className="mt-6 shadow-xl rounded-lg">
                        <CardBody className="p-6 space-y-6">
                            <div className="flex items-start justify-between gap-4 flex-col lg:flex-row lg:items-center">
                                <div>
                                    <Typography variant="h5" className="text-[#2a2e30]">
                                        Analysis Code Mappings
                                    </Typography>
                                    <Typography className="text-sm text-slate-600">
                                        {selectedAccount.account_code} - {selectedAccount.account_name}
                                    </Typography>
                                </div>
                                <Button
                                    onClick={() => setSelectedAccount(null)}
                                    variant="text"
                                    className="text-slate-600 rounded-sm"
                                >
                                    Close
                                </Button>
                            </div>

                            {mappingError && <Alerty open={Boolean(mappingError)} setOpen={setMappingError} color="orange" message={mappingError} />}

                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-end">
                                <div>
                                    <Typography className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1">
                                        Analysis code
                                    </Typography>
                                    <Select
                                        label="Analysis code"
                                        value={mappingAnalysisCode}
                                        onChange={(value) => setMappingAnalysisCode(value)}
                                    >
                                        <Option value="">Select analysis code</Option>
                                        {availableAnalysisCodes.map((code) => (
                                            <Option key={code.code} value={code.code}>
                                                {code.code} {code.description ? `- ${code.description}` : ''}
                                            </Option>
                                        ))}
                                    </Select>
                                </div>
                                <div className="flex items-center gap-3">
                                    <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                                        <input
                                            type="checkbox"
                                            checked={mappingActive}
                                            onChange={(e) => setMappingActive(e.target.checked)}
                                        />
                                        Active
                                    </label>
                                </div>
                                <div className="flex justify-start lg:justify-end">
                                    <Button
                                        onClick={createMapping}
                                        className="bg-[#FF5733] hover:bg-[#e64520] text-white rounded-sm px-4 py-2 shadow-none"
                                        disabled={mappingSaving}
                                    >
                                        Add Mapping
                                    </Button>
                                </div>
                            </div>

                            {mappingLoading ? (
                                <Typography>Loading mappings...</Typography>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full table-auto border-collapse">
                                        <thead>
                                            <tr className="bg-gray-100">
                                                <th className="border px-4 py-2 text-left">Analysis Code</th>
                                                <th className="border px-4 py-2 text-left">Description</th>
                                                <th className="border px-4 py-2 text-center">Status</th>
                                                <th className="border px-4 py-2 text-left">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentAccountMappings.length === 0 ? (
                                                <tr>
                                                    <td colSpan={4} className="border px-4 py-6 text-center text-sm text-gray-500">
                                                        No analysis code mappings for this account.
                                                    </td>
                                                </tr>
                                            ) : currentAccountMappings.map((mapping) => (
                                                <tr key={`${mapping.analysis_code}-${mapping.gl_account_code}`} className="hover:bg-gray-50">
                                                    <td className="border px-4 py-2 font-mono font-bold">{mapping.analysis_code}</td>
                                                    <td className="border px-4 py-2">{mapping.analysis_description || '—'}</td>
                                                    <td className="border px-4 py-2 text-center">
                                                        <Chip
                                                            value={mapping.is_active ? 'Active' : 'Inactive'}
                                                            color={mapping.is_active ? 'green' : 'gray'}
                                                            size="sm"
                                                        />
                                                    </td>
                                                    <td className="border px-4 py-2">
                                                        {mapping.is_active ? (
                                                            <Button
                                                                onClick={() => setMappingDeleteTarget(mapping)}
                                                                size="sm"
                                                                className="bg-transparent shadow-none text-red-700"
                                                            >
                                                                Deactivate
                                                            </Button>
                                                        ) : (
                                                            <Typography className="text-sm text-gray-500">Inactive</Typography>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </CardBody>
                    </Card>
                )}
            </div>

            <Dialog open={Boolean(mappingDeleteTarget)} handler={() => setMappingDeleteTarget(null)} size="sm">
                <DialogHeader>Deactivate mapping</DialogHeader>
                <DialogBody>
                    Deactivate {mappingDeleteTarget?.analysis_code || 'this analysis code'} for {mappingDeleteTarget?.gl_account_code || 'this account'}?
                </DialogBody>
                <DialogFooter>
                    <Button variant="text" onClick={() => setMappingDeleteTarget(null)}>Cancel</Button>
                    <Button onClick={deactivateMapping} disabled={mappingDeleteLoading} className="bg-[#FF5733] text-white">
                        Deactivate
                    </Button>
                </DialogFooter>
            </Dialog>

            {/* Create Modal */}
            <Dialog open={showCreateModal} handler={handleCreateClose} size="lg">
                <DialogHeader className="flex justify-between items-center">
                    Create New Account
                    <button onClick={handleCreateClose} className="text-gray-600 hover:text-gray-800">
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </DialogHeader>
                <DialogBody className="max-h-96 overflow-y-auto space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <Input
                            label="Account Code (5 digits)"
                            value={formData.account_code}
                            onChange={(e) => setFormData({ ...formData, account_code: e.target.value })}
                            error={!!errors.account_code}
                        />
                        {errors.account_code && <p className="text-red-500 text-xs col-span-2">{errors.account_code}</p>}
                    </div>

                    <Input
                        label="Account Name"
                        value={formData.account_name}
                        onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                        error={!!errors.account_name}
                    />
                    {errors.account_name && <p className="text-red-500 text-xs">{errors.account_name}</p>}

                    <div className="grid grid-cols-2 gap-4">
                        <Select
                            label="Account Type"
                            value={formData.account_type}
                            onChange={(value) => setFormData({ ...formData, account_type: value })}
                        >
                            <Option value="ASSET">Asset</Option>
                            <Option value="LIABILITY">Liability</Option>
                            <Option value="EQUITY">Equity</Option>
                            <Option value="REVENUE">Revenue</Option>
                            <Option value="EXPENSE">Expense</Option>
                        </Select>

                        <Select
                            label="GL Class"
                            value={formData.gl_class}
                            onChange={(value) => setFormData({ ...formData, gl_class: value })}
                        >
                            <Option value="BALANCE_SHEET">Balance Sheet</Option>
                            <Option value="INCOME_STATEMENT">Income Statement</Option>
                        </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <Select
                            label="Normal Balance"
                            value={formData.normal_balance}
                            onChange={(value) => setFormData({ ...formData, normal_balance: value })}
                        >
                            <Option value="DEBIT">Debit</Option>
                            <Option value="CREDIT">Credit</Option>
                        </Select>

                        <Input
                            label="Sub-Type"
                            value={formData.sub_type}
                            onChange={(e) => setFormData({ ...formData, sub_type: e.target.value })}
                        />
                    </div>

                    <Input
                        label="Detail Type"
                        value={formData.detail_type}
                        onChange={(e) => setFormData({ ...formData, detail_type: e.target.value })}
                    />

                    <Textarea
                        label="Description"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        rows={3}
                    />
                </DialogBody>
                <DialogFooter>
                    <Button onClick={handleCreateClose} variant="text" className="text-gray-600 rounded-sm">
                        Cancel
                    </Button>
                    <Button
                        onClick={createAccount}
                        className="bg-[#FF5733] text-white rounded-sm px-4 py-2 hover:bg-[#e64520]"
                    >
                        <FontAwesomeIcon icon={faSave} className="mr-2" />
                        Create Account
                    </Button>
                </DialogFooter>
            </Dialog>

            {/* Edit Modal */}
            <Dialog open={showEditModal} handler={handleEditClose} size="lg">
                <DialogHeader className="flex justify-between items-center">
                    Edit Account: {editingAccount?.account_code}
                    <button onClick={handleEditClose} className="text-gray-600 hover:text-gray-800">
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </DialogHeader>
                <DialogBody className="max-h-96 overflow-y-auto space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Typography variant="small" className="font-semibold text-[#2a2e30]">
                                Account Code
                            </Typography>
                            <Typography>{formData.account_code}</Typography>
                        </div>
                        <div>
                            <Typography variant="small" className="font-semibold text-[#2a2e30]">
                                Account Type
                            </Typography>
                            <Typography>{formData.account_type}</Typography>
                        </div>
                    </div>

                    <Input
                        label="Sub-Type"
                        value={formData.sub_type}
                        onChange={(e) => setFormData({ ...formData, sub_type: e.target.value })}
                    />

                    <Input
                        label="Detail Type"
                        value={formData.detail_type}
                        onChange={(e) => setFormData({ ...formData, detail_type: e.target.value })}
                    />

                    <Textarea
                        label="Description"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        rows={3}
                    />

                    <div className="flex items-center space-x-4">
                        <Typography variant="small" className="font-semibold text-[#2a2e30]">
                            Status:
                        </Typography>
                        <Chip
                            value={formData.is_enabled ? 'Enabled' : 'Disabled'}
                            color={formData.is_enabled ? 'green' : 'gray'}
                            size="sm"
                        />
                    </div>
                </DialogBody>
                <DialogFooter>
                    <Button onClick={handleEditClose} variant="text" className="text-gray-600 rounded-sm">
                        Cancel
                    </Button>
                    <Button
                        onClick={updateAccount}
                        className="bg-[#FF5733] text-white rounded-sm px-4 py-2 hover:bg-[#e64520]"
                    >
                        <FontAwesomeIcon icon={faSave} className="mr-2" />
                        Update Account
                    </Button>
                </DialogFooter>
            </Dialog>
        </div>
    );
};

export default ChartOfAccountsManager;
